import { Search, X } from "lucide-react";
import type { Location } from "@/hooks/useLocations";
import {
  bedroomOptions,
  getCategoryOptions,
  maxPriceOptions,
  sanitizePropertySearchFilters,
  type PropertySearchFilters,
} from "@/lib/propertyFilters";
import { cn } from "@/lib/utils";

type PropertyFiltersProps = {
  value: PropertySearchFilters;
  locations: Pick<Location, "id" | "name">[];
  loadingLocations?: boolean;
  onChange: (filters: PropertySearchFilters) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  fixedListingType?: string;
  showLocation?: boolean;
  theme?: "page" | "hero";
  className?: string;
  submitLabel?: string;
};

const PropertyFilters = ({
  value,
  locations,
  loadingLocations = false,
  onChange,
  onSubmit,
  onReset,
  fixedListingType,
  showLocation = true,
  theme = "page",
  className,
  submitLabel = "Search",
}: PropertyFiltersProps) => {
  const isHero = theme === "hero";
  const effectiveListingType = fixedListingType ?? value.listingType;
  const categoryOptions = getCategoryOptions(effectiveListingType);
  const labelClassName = cn(
    "font-body text-xs uppercase tracking-wider",
    isHero ? "text-primary-foreground/80" : "text-muted-foreground",
  );
  const fieldClassName = cn(
    "w-full rounded-md px-4 py-3 font-body text-sm focus:outline-none",
    isHero
      ? "bg-transparent border border-white/20 text-white focus:border-white"
      : "border border-white/30 bg-[#032820] text-white focus:border-white",
  );

  const updateFilters = (next: Partial<PropertySearchFilters>) => {
    onChange(sanitizePropertySearchFilters({ ...value, ...next }, fixedListingType));
  };

  return (
    <div className={cn(isHero ? "" : "rounded-2xl border border-white/20 bg-[#032820] p-4 md:p-6", className)}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        {!fixedListingType && (
          <div className="space-y-2">
            <label className={labelClassName}>Sale / Rental</label>
            <select value={value.listingType} onChange={(e) => updateFilters({ listingType: e.target.value })} className={fieldClassName}>
              <option value="" className="text-foreground">Any</option>
              <option value="sale" className="text-foreground">For Sale</option>
              <option value="rent" className="text-foreground">For Rent</option>
            </select>
          </div>
        )}

        <div className="space-y-2">
          <label className={labelClassName}>Property Type</label>
          <select value={value.category} onChange={(e) => updateFilters({ category: e.target.value })} className={fieldClassName}>
            <option value="" className="text-foreground">Any</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value} className="text-foreground">{option.label}</option>
            ))}
          </select>
        </div>

        {showLocation && (
          <div className="space-y-2">
            <label className={labelClassName}>Location</label>
            <select value={value.locationId} onChange={(e) => updateFilters({ locationId: e.target.value })} disabled={loadingLocations} className={cn(fieldClassName, "disabled:cursor-not-allowed disabled:opacity-60")}>
              <option value="" className="text-foreground">{loadingLocations ? "Loading locations..." : "Any Location"}</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id} className="text-foreground">{location.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-2">
          <label className={labelClassName}>Bedrooms</label>
          <select value={value.bedrooms} onChange={(e) => updateFilters({ bedrooms: e.target.value })} className={fieldClassName}>
            <option value="" className="text-foreground">Any</option>
            {bedroomOptions.map((bedroom) => (
              <option key={bedroom} value={bedroom} className="text-foreground">{bedroom}+</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName}>Max Price</label>
          <select value={value.maxPrice} onChange={(e) => updateFilters({ maxPrice: e.target.value })} className={fieldClassName}>
            <option value="" className="text-foreground">Any</option>
            {maxPriceOptions.map((option) => (
              <option key={option.value} value={option.value} className="text-foreground">{option.label}</option>
            ))}
          </select>
        </div>

        {(onSubmit || onReset) && (
          <div className="flex items-end gap-2">
            {onSubmit && (
              <button type="button" onClick={onSubmit} className="flex-1 bg-white text-[#032820] rounded-md px-4 py-3 font-body text-sm font-medium tracking-wider uppercase hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2">
                <Search className="w-4 h-4" />
                {submitLabel}
              </button>
            )}
            {onReset && (
              <button type="button" onClick={onReset} className={cn("rounded-md px-4 py-3 font-body text-sm font-medium transition-colors inline-flex items-center justify-center gap-2", isHero ? "border border-white/20 text-white hover:bg-white/10" : "border border-white/50 text-white hover:bg-white/20") }>
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyFilters;